import { type IHttpApi, type IHttpApiFetchConfig, type IModelResponse } from '@cian/http-api/shared';
import { type ILogger } from '@cian/logger/shared';
import { Err, GenericError, HttpTimeoutError, Ok, type Result } from '@cian/peperrors/shared';

import { ResponseError } from '../errors';

interface IAppContext {
  httpApi: IHttpApi;
  logger: ILogger;
}

type Enumerate<N extends number, Acc extends number[] = []> = Acc['length'] extends N
  ? Acc[number]
  : Enumerate<N, [...Acc, Acc['length']]>;

type IntRange<F extends number, T extends number> = Exclude<Enumerate<T>, Enumerate<F>>;

interface IModelResponse200<Response> extends IModelResponse<Response> {
  statusCode: 200;
}

interface IModelResponseNot200<Response> extends IModelResponse<Response> {
  statusCode: Exclude<IntRange<100, 599>, 200>;
}

type TFetchFunction<Request extends Object, Response> = (params: {
  httpApi: IHttpApi;
  config: IHttpApiFetchConfig;
  parameters: Request;
}) => Promise<IModelResponse200<Response> | IModelResponseNot200<unknown>>;

/* istanbul ignore next */
export function withTypeSafeApiResult<Request extends Object, Response>(
  fetchFunction: TFetchFunction<Request, Response>,
) {
  return async (
    ctx: IAppContext,
    parameters: Request = {} as Request,
    fetchConfig?: IHttpApiFetchConfig,
  ): Promise<Result<Response, HttpTimeoutError | ResponseError | GenericError>> => {
    try {
      const { httpApi } = ctx;

      const res = await fetchFunction({
        httpApi,
        parameters,
        config: { ...fetchConfig },
      });

      if (res.statusCode === 200) {
        return Ok(res.response);
      }

      return Err(
        new ResponseError({
          message: getErrorMessage(res, `Ошибка получения данных - ${fetchFunction.name}()`),
          domain: fetchFunction.name,
          details: {
            statusCode: res.statusCode,
            response: JSON.stringify(res.response),
          },
        }),
      );
    } catch (error) {
      if (error instanceof HttpTimeoutError) {
        return Err(error);
      }

      ctx.logger.error(error);

      return Err(new GenericError({ message: getErrorMessage(error), type: 'unknown', domain: fetchFunction.name }));
    } finally {
      fetchConfig?.cancellationToken?.destroy();
    }
  };
}

/* istanbul ignore next */
function getErrorMessage(errorLike: unknown, defaultMessage = 'Произошла ошибка'): string {
  switch (typeof errorLike) {
    case 'string':
      return errorLike;
    case 'object':
      if (errorLike === null) {
        return defaultMessage;
      }
      if ('message' in errorLike && typeof errorLike.message === 'string') {
        return errorLike.message;
      } else if (
        'errors' in errorLike &&
        Array.isArray(errorLike.errors) &&
        errorLike.errors.some((error: unknown) => getErrorMessage(error, defaultMessage) !== defaultMessage)
      ) {
        return errorLike.errors.find(
          (error: unknown) => getErrorMessage(error, defaultMessage) !== defaultMessage,
        ) as string;
      }

      return defaultMessage;
    default:
      return defaultMessage;
  }
}
